/*
 * Протокол канала сервер ↔ коннектор 006: JSON-RPC 2.0 по одному исходящему WebSocket
 * (specs/006-agent-client/contracts/connector-protocol.md, research R4). Схемы Zod — одни для сервера и коннектора.
 * Список команд сервера закрыт (FR-025): незнакомое имя — ошибка `-32601`.
 */
import { z } from 'zod';
import { JournalItem } from '../agent-events/record';
import { AGENTS, CONNECTOR_PROTOCOL } from './versions';

export const AgentIdSchema = z.enum(AGENTS);

/** Имя компьютера: ≤ 64 знака, `[A-Za-z0-9._-]` (data-model А.1). */
export const Hostname = z.string().regex(/^[A-Za-z0-9._-]{1,64}$/u);

export const AGENT_STATES = [
  'ready',
  'not_installed',
  'outdated',
  'logged_out',
  'login_expired',
  'not_linked',
  'unknown',
] as const;
export const AgentState = z.enum(AGENT_STATES);
export type AgentState = z.infer<typeof AgentState>;

export const LoginMethod = z.enum(['subscription', 'api_key', 'chatgpt_via_pi', 'other']);

export const AgentHealth = z.strictObject({
  agent: AgentIdSchema,
  state: AgentState,
  version: z.string().max(64).nullable(),
  minVersion: z.string().max(64),
  loginMethod: LoginMethod.nullable(),
  flags: z.strictObject({
    subscriptionBlocked: z.literal(true).optional(),
    readOnly: z.literal(true).optional(),
    otherMcpServers: z.literal(true).optional(),
    /** pi вошёл в Anthropic по подписке Claude: эти модели табель через pi не запускает (FR-060). */
    claudeSubscription: z.literal(true).optional(),
  }),
  /** Команда входа в агента для терминала (FR-010). Строит коннектор. */
  loginCommand: z.string().max(500).nullable(),
  /** Команда входа в MCP табеля по 005. */
  linkCommand: z.string().max(500).nullable(),
  checkedAt: z.iso.datetime(),
});
export type AgentHealth = z.infer<typeof AgentHealth>;

export const ModelInfo = z.strictObject({
  id: z.string().max(200),
  name: z.string().max(200),
  isDefault: z.boolean(),
  efforts: z.array(z.string().max(32)).max(10),
  defaultEffort: z.string().max(32).nullable(),
  provider: z.string().max(64).optional(),
});
export type ModelInfo = z.infer<typeof ModelInfo>;

// ——— Сообщения коннектора → сервер ———

export const HelloParams = z.strictObject({
  protocol: z.number().int(),
  connectorVersion: z.string().max(32),
  os: z.enum(['macos', 'linux']),
  arch: z.string().max(32),
  hostname: Hostname,
  agentsDir: z.string().max(200),
});
export type HelloParams = z.infer<typeof HelloParams>;

export const HelloResult = z.strictObject({
  sessionId: z.string(),
  connectorId: z.number().int(),
  heartbeatMs: z.number().int(),
  leaseMs: z.number().int(),
  requiredVersion: z.string(),
  requiredSha256: z.string(),
  skillVersion: z.string().nullable(),
  limits: z.strictObject({ maxTurnsPerComputer: z.number().int() }),
});
export type HelloResult = z.infer<typeof HelloResult>;

export const HeartbeatParams = z.strictObject({ activeTurns: z.number().int().min(0) });

export const EventsAppendParams = z.strictObject({
  batchKey: z.string().min(1).max(120),
  items: z.array(JournalItem).min(1).max(500),
});
export type EventsAppendParams = z.infer<typeof EventsAppendParams>;
export const EventsAppendResult = z.strictObject({
  ack: z.string(),
  appended: z.number().int(),
  skipped: z.number().int(),
});

export const AgentsChangedParams = z.strictObject({ agents: z.array(AgentHealth) });
export type AgentsChangedParams = z.infer<typeof AgentsChangedParams>;
export const InstallProgressParams = z.strictObject({
  agent: AgentIdSchema,
  lines: z.array(z.string().max(500)).max(200),
  done: z.strictObject({ status: z.enum(['ok', 'failed']) }).optional(),
});
export type InstallProgressParams = z.infer<typeof InstallProgressParams>;

export const CONNECTOR_METHODS = {
  'connector.hello': HelloParams,
  heartbeat: HeartbeatParams,
  'events.append': EventsAppendParams,
  'agents.changed': AgentsChangedParams,
  'install.progress': InstallProgressParams,
} as const;
export type ConnectorMethod = keyof typeof CONNECTOR_METHODS;

// ——— Команды сервера → коннектор (закрытый список, FR-025) ———

const Effort = z.string().max(32).nullable();
export const COMMANDS = {
  'agents.detect': { params: z.strictObject({}), result: z.strictObject({ agents: z.array(AgentHealth) }) },
  'agents.health': {
    params: z.strictObject({ agent: AgentIdSchema.optional() }),
    result: z.strictObject({ agents: z.array(AgentHealth) }),
  },
  'agents.install': {
    params: z.strictObject({ agent: AgentIdSchema, action: z.enum(['install', 'update']) }),
    result: z.strictObject({ started: z.literal(true) }),
  },
  'agents.models': {
    params: z.strictObject({ agent: AgentIdSchema }),
    result: z.strictObject({ models: z.array(ModelInfo) }),
  },
  'thread.start': {
    params: z.strictObject({
      threadId: z.string(),
      agent: AgentIdSchema,
      model: z.string().max(200).nullable(),
      effort: Effort,
      readOnly: z.boolean(),
      skillVersion: z.string().nullable(),
      resumeSessionId: z.string().nullable(),
    }),
    result: z.strictObject({ agentSessionId: z.string().nullable() }),
  },
  'thread.stop': {
    params: z.strictObject({ threadId: z.string(), runId: z.string() }),
    result: z.strictObject({ stopped: z.literal(true) }),
  },
  'turn.submit': {
    params: z.strictObject({
      threadId: z.string(),
      runId: z.string(),
      messageKey: z.string(),
      text: z.string().max(8000),
      continue: z.boolean().optional(),
      model: z.string().max(200).nullable().optional(),
      effort: Effort.optional(),
    }),
    result: z.strictObject({ accepted: z.literal(true) }),
  },
  'turn.steer': {
    params: z.strictObject({
      threadId: z.string(),
      runId: z.string(),
      messageKey: z.string(),
      text: z.string().max(8000),
    }),
    result: z.union([
      z.strictObject({ accepted: z.literal(true) }),
      z.strictObject({ stale: z.literal(true) }),
    ]),
  },
  'interaction.resolve': {
    params: z.strictObject({
      threadId: z.string(),
      interactionId: z.string(),
      decision: z.enum(['allow', 'deny', 'cancel']),
      answers: z.record(z.string(), z.string()).optional(),
      message: z.string().max(500).optional(),
    }),
    result: z.strictObject({ delivered: z.boolean() }),
  },
  'connector.update': {
    params: z.strictObject({ version: z.string(), sha256: z.string() }),
    result: z.strictObject({ scheduled: z.literal(true) }),
  },
  'connector.revoke': { params: z.strictObject({ reason: z.string().max(64) }), result: z.strictObject({}) },
} as const;
export type Command = keyof typeof COMMANDS;
export const COMMAND_NAMES = Object.keys(COMMANDS) as Command[];
export type CommandParams<C extends Command> = z.infer<(typeof COMMANDS)[C]['params']>;
export type CommandResult<C extends Command> = z.infer<(typeof COMMANDS)[C]['result']>;

/** Срок ответа на команду (FR-024): 30 с, кроме отмеченных в contracts/connector-protocol.md §3. */
export const COMMAND_TIMEOUT_MS: Readonly<Record<Command, number>> = {
  'agents.detect': 30_000,
  'agents.health': 30_000,
  'agents.install': 30_000,
  'agents.models': 60_000,
  'thread.start': 120_000,
  'thread.stop': 60_000,
  'turn.submit': 30_000,
  'turn.steer': 30_000,
  'interaction.resolve': 30_000,
  'connector.update': 30_000,
  'connector.revoke': 5_000,
};

// ——— JSON-RPC ———

export const RpcId = z.union([z.string().max(64), z.number().int()]);
export const RpcRequest = z.strictObject({
  jsonrpc: z.literal('2.0'),
  id: RpcId.optional(),
  method: z.string().max(64),
  params: z.unknown().optional(),
});
export const RpcResponse = z.strictObject({
  jsonrpc: z.literal('2.0'),
  id: RpcId,
  result: z.unknown().optional(),
  error: z
    .strictObject({ code: z.number().int(), message: z.string(), data: z.unknown().optional() })
    .optional(),
});
export type RpcRequest = z.infer<typeof RpcRequest>;
export type RpcResponse = z.infer<typeof RpcResponse>;

export const RPC_ERRORS = {
  parse: -32700,
  invalidRequest: -32600,
  methodNotFound: -32601,
  invalidParams: -32602,
  internal: -32603,
  /** Пакет событий не прошёл правила AG-UI и FR-019. */
  invalidEvents: -32010,
  /** Разговора нет на этом компьютере. */
  threadNotFound: -32011,
  /** Версия протокола не та — самообновление. */
  protocolMismatch: -32012,
  /** Сначала `connector.hello`. */
  helloRequired: -32013,
  /** Уже идёт установка агента (FR-009: одна за раз). */
  installRunning: -32020,
  /** Агент не установлен или не подходит по версии. */
  agentNotReady: -32021,
  /** Агент не дал список моделей. */
  modelsUnavailable: -32022,
} as const;

/** Предел сообщения канала — 1 МБ (contracts/connector-protocol.md §2). */
export const WS_MAX_MESSAGE_BYTES = 1_048_576;
export const WS_SUBPROTOCOL = 'tabel-connector.v1';
export const HEARTBEAT_MS = 5_000;
export const LEASE_MS = 30_000;
export { CONNECTOR_PROTOCOL };

/** Разбор входящего кадра: один объект JSON-RPC; массив (пакет) — отказ. */
export function parseFrame(raw: string): RpcRequest | RpcResponse | { error: string } {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return { error: 'parse' };
  }
  if (Array.isArray(data)) return { error: 'batch' };
  if (data && typeof data === 'object' && 'method' in data) {
    const r = RpcRequest.safeParse(data);
    return r.success ? r.data : { error: 'invalid_request' };
  }
  const r = RpcResponse.safeParse(data);
  return r.success ? r.data : { error: 'invalid_request' };
}
